import { Location, NavigateFunction } from "react-router-dom"
import { City } from "../types/entities/city"
import { Cinema } from "../types/entities/cinema"
import { appStore } from "../store/appStore"
import { findNearestCinema, getCurrentPosition } from "./nearestLocation"

type RequestResult<T> = { status: "success"; data: T } | { status: "error"; error: { message: string } }

type RequestData = <T>(opts: {
    type: "GET" | "POST" | "PUT" | "DELETE" | "PATCH"
    path: string
    disableError?: boolean
}) => Promise<RequestResult<T>>

let geoAttemptedThisSession = false

export function resetLocationGeoAttempt(): void {
    geoAttemptedThisSession = false
}

async function tryGeolocation(
    requestData: RequestData,
    timeoutMs?: number,
): Promise<{ city: City; cinema: Cinema } | null> {
    if (geoAttemptedThisSession) return null
    geoAttemptedThisSession = true

    const pos = await getCurrentPosition(timeoutMs)
    if (!pos) return null

    const [cityRes, cinemaRes] = await Promise.all([
        requestData<City[]>({ type: "GET", path: "city", disableError: true }),
        requestData<Cinema[]>({ type: "GET", path: "cinema", disableError: true }),
    ])

    if (cityRes.status !== "success" || cinemaRes.status !== "success") return null
    if (!cityRes.data?.length || !cinemaRes.data?.length) return null

    return findNearestCinema(pos, cityRes.data, cinemaRes.data)
}

async function isGeoPermissionGranted(): Promise<boolean> {
    if (typeof navigator === "undefined" || !navigator.permissions?.query) return false
    try {
        const status = await navigator.permissions.query({ name: "geolocation" })
        return status.state === "granted"
    } catch {
        return false
    }
}

/**
 * Тихое определение города для главной страницы: срабатывает только если
 * гео-разрешение уже выдано — без запроса разрешения и без открытия пикера.
 * Возвращает true, если город определён и сохранён в store.
 */
export async function resolveLocationSilently(requestData: RequestData): Promise<boolean> {
    const { locationSelected, setLocation } = appStore.getState()
    if (locationSelected) return true

    if (!(await isGeoPermissionGranted())) return false

    /* Короткий таймаут: не держим посетителя на лоадере вместо лендинга */
    const nearest = await tryGeolocation(requestData, 4000)
    if (!nearest) return false

    setLocation(nearest)
    return true
}

/** Подбирает кинотеатр по геолокации или открывает /location поверх текущего экрана. */
export async function resolveLocationOrOpenPicker(
    requestData: RequestData,
    navigate: NavigateFunction,
    location: Location,
): Promise<boolean> {
    const { locationSelected, setLocation } = appStore.getState()
    if (locationSelected) return true

    const nearest = await tryGeolocation(requestData)
    if (nearest) {
        setLocation(nearest)
        return true
    }

    navigate("/location", {
        state: {
            backgroundLocation: location,
            returnTo: `${location.pathname}${location.search}${location.hash}`,
        },
    })
    return false
}

/** Для CinemaPicker: подсветить ближайший город без сохранения в store. */
export async function suggestNearestCity(requestData: RequestData): Promise<City | null> {
    const pos = await getCurrentPosition(6000)
    if (!pos) return null

    const [cityRes, cinemaRes] = await Promise.all([
        requestData<City[]>({ type: "GET", path: "city", disableError: true }),
        requestData<Cinema[]>({ type: "GET", path: "cinema", disableError: true }),
    ])

    if (cityRes.status !== "success" || cinemaRes.status !== "success") return null
    if (!cityRes.data?.length || !cinemaRes.data?.length) return null

    const nearest = findNearestCinema(pos, cityRes.data, cinemaRes.data)
    return nearest?.city ?? null
}
